import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * RegisterAssetModal
 *
 * Props:
 *   open        {boolean}
 *   onClose     {() => void}
 *   onSuccess   {({ type, asset?, created?, failed? }) => void}
 *   token       {string}
 *   activeOrg   {object|null}
 *
 * API:
 *   Single → POST /api/v1/inventory/assets
 *   Bulk   → POST /api/v1/inventory/assets  (N sequential calls)
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Divider,
  Tab,
  Tabs,
  IconButton,
  CircularProgress,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Paper,
  Tooltip,
  Alert,
  MenuItem,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { useAuth } from "../../../../auth/useAuth";
import AssetTypeManagerModal from "./AssetTypeManagerModal";
import { useNotification } from "../../../../context/NotificationContext";
const API_BASE = `${APP_INVENTORY_API_BASE}`;

// ─── Column definitions ───────────────────────────────────────────────────────

const BULK_COLS = [
  {
    key: "asset_type_code",
    label: "Asset type code",
    required: false,
    width: 160,
    hint: "SURVITAN",
  },
  {
    key: "asset_type",
    label: "Asset type",
    required: false,
    width: 140,
    hint: "Pulse Oximeter",
  },
  {
    key: "serial_number",
    label: "Serial no.",
    required: true,
    width: 130,
    hint: "SN-MED-001",
  },
  {
    key: "model",
    label: "Model",
    required: true,
    width: 120,
    hint: "OxyPro X1",
  },
  {
    key: "manufacturer",
    label: "Manufacturer",
    required: false,
    width: 120,
    hint: "CoreData",
  },
  {
    key: "manufacturing_batch",
    label: "Batch",
    required: false,
    width: 110,
    hint: "BATCH-2024-Q4",
  },
  {
    key: "imei",
    label: "IMEI",
    required: false,
    width: 140,
    hint: "358240051111110",
  },
  {
    key: "mac_address",
    label: "MAC address",
    required: false,
    width: 140,
    hint: "AA:BB:CC:DD:EE:FF",
  },
  {
    key: "qr_code",
    label: "QR code",
    required: false,
    width: 130,
    hint: "QR-SN-MED-001",
  },

  {
    key: "purchase_order",
    label: "PO number",
    required: false,
    width: 110,
    hint: "PO-2024-001",
  },
  {
    key: "invoice_number",
    label: "Invoice no.",
    required: false,
    width: 120,
    hint: "INV-2024-001",
  },

  {
    key: "warranty_start",
    label: "Warranty start",
    required: false,
    width: 110,
    hint: "2024-01-15",
  },
  {
    key: "warranty_end",
    label: "Warranty end",
    required: false,
    width: 110,
    hint: "2026-01-15",
  },
];

const SINGLE_FIELDS = [
  // col 1 — Identity
  {
    name: "asset_type",
    label: "Asset type",
    placeholder: "e.g. Pulse Oximeter",
    required: true,
    col: 1,
  },
  {
    name: "model",
    label: "Model",
    placeholder: "e.g. OxyPro X1",
    required: true,
    col: 1,
  },
  {
    name: "serial_number",
    label: "Serial number",
    placeholder: "SN-XXX-000",
    required: true,
    col: 1,
  },
  {
    name: "manufacturer",
    label: "Manufacturer",
    placeholder: "CoreData",
    required: false,
    col: 1,
  },
  {
    name: "manufacturing_batch",
    label: "Batch number",
    placeholder: "BATCH-2024-Q4",
    required: false,
    col: 1,
  },
  // col 2 — Hardware IDs
  {
    name: "imei",
    label: "IMEI",
    placeholder: "15-digit IMEI",
    required: false,
    col: 2,
  },
  {
    name: "mac_address",
    label: "MAC address",
    placeholder: "AA:BB:CC:DD:EE:FF",
    required: false,
    col: 2,
  },
  {
    name: "qr_code",
    label: "QR code",
    placeholder: "QR-SN-XXX-000",
    required: false,
    col: 2,
  },
  {
    name: "purchase_order",
    label: "Purchase order",
    placeholder: "PO-2024-001",
    required: false,
    col: 2,
  },
  {
    name: "invoice_number",
    label: "Invoice number",
    placeholder: "INV-2024-001",
    required: false,
    col: 2,
  },
  // col 3 — Warranty (full-width row)
  {
    name: "warranty_start",
    label: "Warranty start",
    required: false,
    col: 3,
  },
  {
    name: "warranty_end",
    label: "Warranty end",
    required: false,
    col: 3,
  },
];

// ─── Validation ───────────────────────────────────────────────────────────────

const RE = {
  sn: /^[A-Za-z0-9\-_]{3,100}$/,
  imei: /^\d{15}$/,
  mac: /^([0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
};

function validateSingle(form) {
  const e = {};
  if (!form.asset_type_id?.trim() && !form.asset_type?.trim())
    e.asset_type = "Required";
  if (!form.serial_number?.trim()) e.serial_number = "Required";
  if (!form.model?.trim()) e.model = "Required";
  if (form.serial_number && !RE.sn.test(form.serial_number.trim()))
    e.serial_number =
      "Alphanumeric, hyphens and underscores only (3–100 chars)";
  if (form.imei?.trim() && !RE.imei.test(form.imei.trim()))
    e.imei = "Must be exactly 15 digits";
  if (form.mac_address?.trim() && !RE.mac.test(form.mac_address.trim()))
    e.mac_address = "Format: AA:BB:CC:DD:EE:FF";
  if (form.warranty_start?.trim() && !RE.date.test(form.warranty_start.trim()))
    e.warranty_start = "Format: YYYY-MM-DD";
  if (form.warranty_end?.trim() && !RE.date.test(form.warranty_end.trim()))
    e.warranty_end = "Format: YYYY-MM-DD";
  if (
    form.warranty_start &&
    form.warranty_end &&
    RE.date.test(form.warranty_start) &&
    RE.date.test(form.warranty_end) &&
    new Date(form.warranty_end) <= new Date(form.warranty_start)
  )
    e.warranty_end = "Must be after warranty start";
  return e;
}

function renderDynamicField(field, value, error, onChange) {
  if (field.data_type === "boolean") {
    return (
      <TextField
        size="small"
        select
        fullWidth
        label={field.label}
        value={String(value ?? "")}
        error={!!error}
        helperText={error || field.help_text}
        onChange={(e) => onChange(field.field_key, e.target.value === "true")}
      >
        <MenuItem value="true">Yes</MenuItem>
        <MenuItem value="false">No</MenuItem>
      </TextField>
    );
  }
  if (field.data_type === "select") {
    return (
      <TextField
        size="small"
        select
        fullWidth
        label={field.label}
        value={value ?? ""}
        error={!!error}
        helperText={error || field.help_text}
        onChange={(e) => onChange(field.field_key, e.target.value)}
      >
        {(field.options || []).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      type={
        field.data_type === "number"
          ? "number"
          : field.data_type === "date"
            ? "date"
            : "text"
      }
      label={field.label}
      placeholder={field.placeholder || ""}
      value={value ?? ""}
      error={!!error}
      helperText={error || field.help_text}
      InputLabelProps={
        field.data_type === "date" ? { shrink: true } : undefined
      }
      onChange={(e) => onChange(field.field_key, e.target.value)}
    />
  );
}

function validateBulkRow(row, allSerials) {
  const e = {};
  if (!row.asset_type?.trim() && !row.asset_type_code?.trim()) {
    e.asset_type = "Provide asset_type or asset_type_code";
  }
  if (!row.serial_number?.trim()) e.serial_number = "Required";
  if (!row.model?.trim()) e.model = "Required";
  const sn = row.serial_number?.trim();
  if (sn && !RE.sn.test(sn)) e.serial_number = "Invalid format";
  if (row.imei?.trim() && !RE.imei.test(row.imei.trim()))
    e.imei = "15 digits only";
  if (row.mac_address?.trim() && !RE.mac.test(row.mac_address.trim()))
    e.mac_address = "AA:BB:CC:DD:EE:FF";
  if (sn && allSerials.filter((s) => s === sn).length > 1)
    e.serial_number = "Duplicate in batch";
  return e;
}

function normalizeAssetTypeCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getMatchedAssetType(row, assetTypes) {
  const code = normalizeAssetTypeCode(row.asset_type_code);
  const name = String(row.asset_type || "")
    .trim()
    .toLowerCase();
  if (code) {
    const matchByCode = assetTypes.find(
      (assetType) => normalizeAssetTypeCode(assetType.code) === code,
    );
    if (matchByCode) return matchByCode;
  }
  if (name) {
    const matchByName = assetTypes.find(
      (assetType) =>
        String(assetType.name || "")
          .trim()
          .toLowerCase() === name,
    );
    if (matchByName) return matchByName;
  }
  return null;
}

function validateBulkRowWithTypes(row, assetTypes) {
  const matchedAssetType = getMatchedAssetType(row, assetTypes);
  const e = validateBulkRow(row, []);
  if (
    !matchedAssetType &&
    (row.asset_type_code?.trim() || row.asset_type?.trim())
  ) {
    e.asset_type = "Unknown asset type";
  }
  (matchedAssetType?.fields || []).forEach((field) => {
    const value = row[field.field_key];
    if (field.required && !String(value ?? "").trim()) {
      e[field.field_key] = "Required";
    }
  });
  return { matchedAssetType, errors: e };
}

function revalidateAll(rows, assetTypes = []) {
  const all = rows.map((r) => r.serial_number?.trim());
  return rows.map((r) => {
    const baseErrors = validateBulkRow(r, all);
    const matchedAssetType = getMatchedAssetType(r, assetTypes);
    const errors = { ...baseErrors };
    if (
      !matchedAssetType &&
      (r.asset_type_code?.trim() || r.asset_type?.trim())
    ) {
      errors.asset_type = "Unknown asset type";
    }
    (matchedAssetType?.fields || []).forEach((field) => {
      const value = r[field.field_key];
      if (field.required && !String(value ?? "").trim()) {
        errors[field.field_key] = "Required";
      }
    });
    return { ...r, _matchedAssetType: matchedAssetType, _errors: errors };
  });
}

function annotateRows(parsed, assetTypes = []) {
  return revalidateAll(
    parsed.map((r, i) => ({
      ...r,
      _id: `r${i}-${Date.now()}`,
      _skip: false,
      _status: "pending", // pending | uploading | success | error
      _serverError: "",
    })),
    assetTypes,
  );
}

// ─── CSV helpers ──────────────────────────────────────────────────────────────

function csvTemplate() {
  return [
    BULK_COLS.map((c) => c.key).join(","),
    BULK_COLS.map((c) => c.hint).join(","),
  ].join("\n");
}

function downloadTemplate() {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csvTemplate()], { type: "text/csv" }));
  a.download = "asset_upload_template.csv";
  a.click();
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2)
    throw new Error("CSV needs a header row + at least one data row");
  const headers = lines[0]
    .split(",")
    .map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  const missing = ["serial_number", "model"].filter(
    (k) => !headers.includes(k),
  );
  if (missing.length)
    throw new Error(`Missing required columns: ${missing.join(", ")}`);
  if (!headers.includes("asset_type") && !headers.includes("asset_type_code")) {
    throw new Error("CSV must include asset_type or asset_type_code");
  }
  if (lines.length - 1 > 500) throw new Error("Maximum 500 rows per upload");
  return lines
    .slice(1)
    .filter((l) => l.trim())
    .map((line) => {
      const vals = line.split(",");
      const row = {};
      headers.forEach((h, j) => {
        row[h] = (vals[j] || "").trim();
      });
      return row;
    });
}

function downloadErrorReport(failedRows) {
  if (!failedRows?.length) return;
  const extraKeys = Array.from(
    new Set(
      failedRows.flatMap((row) =>
        Object.keys(row).filter(
          (key) =>
            !key.startsWith("_") && !BULK_COLS.some((c) => c.key === key),
        ),
      ),
    ),
  );
  const exportKeys = [...BULK_COLS.map((c) => c.key), ...extraKeys];
  const header = [...exportKeys, "error"].join(",");
  const lines = failedRows.map((r) =>
    [...exportKeys.map((key) => r[key] || ""), r._serverError || ""].join(","),
  );
  const a = document.createElement("a");
  a.href = URL.createObjectURL(
    new Blob([[header, ...lines].join("\n")], { type: "text/csv" }),
  );
  a.download = "upload_errors.csv";
  a.click();
}

// ─────────────────────────────────────────────────────────────────────────────
// SINGLE FORM
// ─────────────────────────────────────────────────────────────────────────────

function SingleForm({
  token,
  activeOrg,
  onClose,
  onSuccess,
  assetTypes,
  onManageAssetTypes,
  canManageAssetTypes,
}) {
  const { showNotification } = useNotification();
  const emptyForm = Object.fromEntries(SINGLE_FIELDS.map((f) => [f.name, ""]));
  const [form, setForm] = useState({
    ...emptyForm,
    asset_type_id: "",
    manufacturer: "CoreData",
    custom_attributes: {},
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const selectedAssetType = useMemo(
    () => assetTypes.find((type) => type.id === form.asset_type_id) || null,
    [assetTypes, form.asset_type_id],
  );

  const handleChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  const handleAssetTypeChange = (_, assetType) => {
    const seededAttributes = {};
    (assetType?.fields || []).forEach((field) => {
      seededAttributes[field.field_key] =
        field.default_value ?? (field.data_type === "boolean" ? false : "");
    });
    setForm((prev) => ({
      ...prev,
      asset_type_id: assetType?.id || "",
      asset_type: assetType?.name || "",
      custom_attributes: seededAttributes,
    }));
    setErrors((prev) => ({ ...prev, asset_type: "" }));
  };

  const handleCustomAttributeChange = (fieldKey, value) => {
    setForm((prev) => ({
      ...prev,
      custom_attributes: {
        ...(prev.custom_attributes || {}),
        [fieldKey]: value,
      },
    }));
    setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleSubmit = async () => {
    const e = validateSingle(form);
    (selectedAssetType?.fields || []).forEach((field) => {
      if (field.required && !form.custom_attributes?.[field.field_key]) {
        e[field.field_key] = "Required";
      }
    });
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        asset_type_id: form.asset_type_id || undefined,
        asset_type: selectedAssetType?.name || form.asset_type,
        custom_attributes: form.custom_attributes || {},
      };
      const res = await axios.post(`${API_BASE}/assets/${activeOrg.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
        },
      });
      setDone(true);
      setTimeout(() => {
        onSuccess({ type: "single", asset: res.data });
        onClose();
      }, 900);
    } catch (err) {
      const msg =
        err.response?.data?.error || "Server error — please try again";
      setErrors({ serial_number: msg });
      setSaving(false);
    }
  };

  const col1 = SINGLE_FIELDS.filter((f) => f.col === 1);
  const col2 = SINGLE_FIELDS.filter((f) => f.col === 2);
  const col3 = SINGLE_FIELDS.filter((f) => f.col === 3);

  return (
    <>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
        >
          {/* Identity */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              display="block"
              mb={1.5}
            >
              Identity
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                options={assetTypes}
                value={selectedAssetType}
                onChange={handleAssetTypeChange}
                getOptionLabel={(option) =>
                  option ? `${option.name} (${option.category})` : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Asset type"
                    required
                    error={!!errors.asset_type}
                    helperText={
                      errors.asset_type ||
                      "Choose a standard inventory asset type."
                    }
                  />
                )}
              />
              {canManageAssetTypes ? (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={onManageAssetTypes}
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  Create asset type
                </Button>
              ) : null}
              {col1
                .filter((f) => f.name !== "asset_type")
                .map((f) => (
                  <TextField
                    key={f.name}
                    size="small"
                    fullWidth
                    label={f.label}
                    placeholder={f.placeholder}
                    required={f.required}
                    value={form[f.name]}
                    error={!!errors[f.name]}
                    helperText={errors[f.name]}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                  />
                ))}
            </Stack>
          </Box>

          {/* Hardware IDs */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              display="block"
              mb={1.5}
            >
              Hardware IDs
            </Typography>
            <Stack spacing={2}>
              {col2.map((f) => (
                <TextField
                  key={f.name}
                  size="small"
                  fullWidth
                  label={f.label}
                  placeholder={f.placeholder}
                  value={form[f.name]}
                  error={!!errors[f.name]}
                  helperText={errors[f.name]}
                  onChange={(e) => handleChange(f.name, e.target.value)}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Warranty */}
        <Divider sx={{ my: 2.5 }} />
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={700}
          display="block"
          mb={1.5}
        >
          Warranty
        </Typography>
        {/* ✅ FIX: was rendering stray dateFrom/handleFilterChange variables */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
  <Box
    display="grid"
    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
    gap={2}
  >
    {/* Warranty Start */}
    <DatePicker
      label="Warranty start"
      value={form.warranty_start ? dayjs(form.warranty_start) : null}
      onChange={(newValue) =>
        handleChange(
          "warranty_start",
          newValue ? newValue.format("YYYY-MM-DD") : ""
        )
      }
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          error: !!errors.warranty_start,
          helperText: errors.warranty_start,
        },
      }}
    />

    {/* Warranty End */}
    <DatePicker
      label="Warranty end"
      value={form.warranty_end ? dayjs(form.warranty_end) : null}
      onChange={(newValue) =>
        handleChange(
          "warranty_end",
          newValue ? newValue.format("YYYY-MM-DD") : ""
        )
      }
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          error: !!errors.warranty_end,
          helperText: errors.warranty_end,
        },
      }}
    />
  </Box>
</LocalizationProvider>

        {selectedAssetType?.fields?.length ? (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              display="block"
              mb={1.5}
            >
              Type-specific metadata
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={2.5}
            >
              {selectedAssetType.fields.map((field) => (
                <Box key={field.id || field.field_key}>
                  {renderDynamicField(
                    field,
                    form.custom_attributes?.[field.field_key],
                    errors[field.field_key],
                    handleCustomAttributeChange,
                  )}
                </Box>
              ))}
            </Box>
          </>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          fullWidth={false}
          sx={{ textTransform: "none" ,color:"#1976d2"}}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={saving || done}
          fullWidth={false}
          startIcon={
            done ? (
              <CheckCircleIcon fontSize="small" />
            ) : saving ? (
              <CircularProgress size={15} color="inherit" />
            ) : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            minWidth: { xs: "100%", sm: 150 },
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            "&:hover": { bgcolor: done ? "success.dark" : undefined },
          }}
        >
          {done ? "Registered!" : saving ? "Registering…" : "Register asset"}
        </Button>
      </DialogActions>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BULK FORM
// ─────────────────────────────────────────────────────────────────────────────

const STAGE = {
  UPLOAD: "upload",
  PREVIEW: "preview",
  SUBMITTING: "submitting",
  DONE: "done",
};

function BulkForm({ token, activeOrg, onClose, onSuccess, assetTypes }) {
  const fileRef = useRef();
  const editRef = useRef();
  const [stage, setStage] = useState(STAGE.UPLOAD);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [editCell, setEditCell] = useState(null); // { rowId, key }

  useEffect(() => {
    if (editCell && editRef.current) editRef.current.focus();
  }, [editCell]);

  const validCount = rows.filter(
    (r) => !r._skip && Object.keys(r._errors).length === 0,
  ).length;
  const invalidCount = rows.filter(
    (r) => !r._skip && Object.keys(r._errors).length > 0,
  ).length;
  const skippedCount = rows.filter((r) => r._skip).length;

  const processFile = useCallback(
    (file) => {
      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        setParseError("Only .csv files accepted");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setParseError("File must be under 5 MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          setRows(annotateRows(parseCSV(ev.target.result), assetTypes));
          setParseError("");
          setStage(STAGE.PREVIEW);
        } catch (err) {
          setParseError(err.message);
        }
      };
      reader.readAsText(file);
    },
    [assetTypes],
  );

  const updateCell = useCallback(
    (rowId, key, value) => {
      setRows((prev) =>
        revalidateAll(
          prev.map((r) => (r._id !== rowId ? r : { ...r, [key]: value })),
          assetTypes,
        ),
      );
    },
    [assetTypes],
  );

  const handleSubmit = useCallback(async () => {
    const toSend = rows.filter(
      (r) => !r._skip && Object.keys(r._errors).length === 0,
    );
    if (!toSend.length) return;
    setStage(STAGE.SUBMITTING);
    let created = 0,
      failed = 0;
    const failedRows = [];

    for (let i = 0; i < toSend.length; i++) {
      const row = toSend[i];
      setRows((prev) =>
        prev.map((r) =>
          r._id === row._id ? { ...r, _status: "uploading" } : r,
        ),
      );
      setProgress(Math.round((i / toSend.length) * 100));
      try {
        const matchedAssetType =
          row._matchedAssetType || getMatchedAssetType(row, assetTypes);
        const standardKeys = new Set(
          BULK_COLS.map((c) => c.key).concat([
            "_id",
            "_errors",
            "_skip",
            "_status",
            "_serverError",
            "_matchedAssetType",
          ]),
        );
        const customAttributes = Object.fromEntries(
          Object.entries(row).filter(
            ([key, value]) => !standardKeys.has(key) && value !== "",
          ),
        );
        const payload = {
          asset_type_id: matchedAssetType?.id,
          asset_type: matchedAssetType?.name || row.asset_type,
          serial_number: row.serial_number,
          model: row.model,
          manufacturer: row.manufacturer,
          manufacturing_batch: row.manufacturing_batch,
          imei: row.imei,
          mac_address: row.mac_address,
          qr_code: row.qr_code,
          purchase_order: row.purchase_order,
          warranty_start: row.warranty_start,
          warranty_end: row.warranty_end,
          custom_attributes: customAttributes,
        };
        await axios.post(`${API_BASE}/assets/${activeOrg.id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
          },
        });
        setRows((prev) =>
          prev.map((r) =>
            r._id === row._id ? { ...r, _status: "success" } : r,
          ),
        );
        created++;
      } catch (err) {
        const msg = err.response?.data?.error || "Server error";
        setRows((prev) =>
          prev.map((r) =>
            r._id === row._id
              ? { ...r, _status: "error", _serverError: msg }
              : r,
          ),
        );
        failedRows.push({ ...row, _serverError: msg });
        failed++;
      }
    }

    setProgress(100);
    setResults({
      created,
      failed,
      skipped: rows.filter((r) => r._skip).length,
      failedRows,
    });
    setStage(STAGE.DONE);
    if (created > 0) onSuccess({ type: "bulk", created, failed });
  }, [rows, token, activeOrg, onSuccess, assetTypes]);

  const isSubmitting = stage === STAGE.SUBMITTING;
  const isDone = stage === STAGE.DONE;
  const visibleCols = BULK_COLS.slice(0, 6);

  // ── Upload screen ──
  if (stage === STAGE.UPLOAD)
    return (
      <>
        <DialogContent dividers>
          {/* Template banner */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={1.5}
            p={2}
            mb={2.5}
            borderRadius={2}
            sx={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="primary.main"
                gutterBottom
              >
                Download the template first
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Required columns:{" "}
                {["asset_type_code / asset_type", "serial_number", "model"].map(
                  (k) => (
                    <Box
                      key={k}
                      component="code"
                      sx={{
                        mx: 0.3,
                        px: 0.6,
                        borderRadius: 0.5,
                        fontSize: 11,
                        fontFamily: "monospace",
                        bgcolor: "action.hover",
                      }}
                    >
                      {k}
                    </Box>
                  ),
                )}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                ml: 2,
              }}
            >
              Template
            </Button>
          </Box>

          {/* Drop zone */}
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              processFile(e.dataTransfer.files[0]);
            }}
            onClick={() => fileRef.current.click()}
            sx={{
              border: "2px dashed",
              borderColor: dragOver ? "primary.main" : "divider",
              borderRadius: 3,
              p: { xs: 2, sm: 5 },
              textAlign: "center",
              cursor: "pointer",
              bgcolor: dragOver ? "action.hover" : "background.default",
              transition: "all 0.2s",
              mb: 2.5,
              "&:hover": {
                borderColor: "primary.light",
                bgcolor: "action.hover",
              },
            }}
          >
            <UploadFileIcon
              sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
            />
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {dragOver
                ? "Drop your CSV here"
                : "Drag & drop your CSV, or click to browse"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CSV only · max 500 rows · max 5 MB
            </Typography>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => processFile(e.target.files[0])}
            />
          </Box>

          {parseError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {parseError}
            </Alert>
          )}

          {/* Column reference */}
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            display="block"
            mb={1}
          >
            Column reference
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 200,
              overflowY: "auto",
              overflowX:"auto"
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      bgcolor: "background.default",
                      fontWeight: 700,
                      fontSize: 11,
                    },
                  }}
                >
                  <TableCell>Column</TableCell>
                  <TableCell>Required</TableCell>
                  <TableCell>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {BULK_COLS.map((c) => (
                  <TableRow key={c.key} hover>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "primary.main",
                      }}
                    >
                      {c.key}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.required ? "Required" : "Optional"}
                        size="small"
                        color={c.required ? "error" : "default"}
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: "text.secondary",
                      }}
                    >
                      {c.hint}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </>
    );

  // ── Preview / Submitting / Done ──
  return (
    <>
      <DialogContent dividers sx={{ p: 0 }}>
        {/* Summary + controls bar */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          px={2.5}
          py={1.5}
          gap={1.5}
          sx={{ borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={<CheckCircleIcon />}
              label={`${validCount} valid`}
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<ErrorIcon />}
              label={`${invalidCount} errors`}
              color="error"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${skippedCount} skipped`}
              size="small"
              variant="outlined"
            />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {!isSubmitting && !isDone && invalidCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                sx={{ textTransform: "none", fontSize: 12 }}
                onClick={() =>
                  setRows((prev) =>
                    prev.map((r) =>
                      Object.keys(r._errors).length > 0
                        ? { ...r, _skip: true }
                        : r,
                    ),
                  )
                }
              >
                Skip all invalid
              </Button>
            )}
            {!isSubmitting && !isDone && (
              <Button
                size="small"
                color="inherit"
                sx={{ textTransform: "none", fontSize: 12 }}
                onClick={() => {
                  setStage(STAGE.UPLOAD);
                  setRows([]);
                  setParseError("");
                }}
              >
                ← Re-upload
              </Button>
            )}
            {isDone && results?.failed > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<DownloadIcon />}
                sx={{ textTransform: "none", fontSize: 12 }}
                onClick={() => downloadErrorReport(results.failedRows)}
              >
                Error report
              </Button>
            )}
          </Stack>
        </Box>

        {/* Progress bar */}
        {isSubmitting && (
          <Box
            px={2.5}
            py={1.5}
            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Uploading assets…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ borderRadius: 1, height: 5 }}
            />
          </Box>
        )}

        {/* Done summary */}
        {isDone && (
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr 1fr"
            gap={{ xs: 1, sm: 2 }}
            p={{ xs: 1.5, sm: 2.5 }}
            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
          >
            {[
              { label: "Created", val: results.created, color: "success.main" },
              { label: "Failed", val: results.failed, color: "error.main" },
              {
                label: "Skipped",
                val: results.skipped,
                color: "text.secondary",
              },
            ].map((s) => (
              <Box
                key={s.label}
                textAlign="center"
                p={{ xs: 1, sm: 1.5 }}
                borderRadius={2}
                sx={{ bgcolor: "action.hover" }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={s.color}
                  sx={{ fontSize: { xs: "1.4rem", sm: "2.125rem" } }}
                >
                  {s.val}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Preview table */}
        <Box
          sx={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: 380,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: "background.default" }}
                />
                <TableCell
                  sx={{
                    width: 36,
                    fontWeight: 700,
                    fontSize: 11,
                    color: "text.secondary",
                    bgcolor: "background.default",
                  }}
                >
                  #
                </TableCell>
                {visibleCols.map((c) => (
                  <TableCell
                    key={c.key}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      bgcolor: "background.default",
                      minWidth: c.width,
                    }}
                  >
                    {c.label}
                    {c.required && (
                      <Box
                        component="span"
                        sx={{ color: "error.main", ml: 0.3 }}
                      >
                        *
                      </Box>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "text.secondary",
                    bgcolor: "background.default",
                    width: 90,
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const hasErr = Object.keys(row._errors).length > 0;
                return (
                  <TableRow
                    key={row._id}
                    sx={{
                      opacity: row._skip ? 0.4 : 1,
                      bgcolor:
                        !row._skip && hasErr && !isDone
                          ? "error.50"
                          : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      {!isSubmitting && !isDone && (
                        <Checkbox
                          size="small"
                          checked={row._skip}
                          onChange={() =>
                            setRows((prev) =>
                              revalidateAll(
                                prev.map((r) =>
                                  r._id === row._id
                                    ? { ...r, _skip: !r._skip }
                                    : r,
                                ),
                                assetTypes,
                              ),
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight={600}
                      >
                        {idx + 1}
                      </Typography>
                    </TableCell>

                    {visibleCols.map((col) => {
                      const isEditing =
                        editCell?.rowId === row._id &&
                        editCell?.key === col.key;
                      const err = row._errors[col.key];
                      return (
                        <TableCell
                          key={col.key}
                          onClick={() =>
                            !isSubmitting &&
                            !isDone &&
                            setEditCell({ rowId: row._id, key: col.key })
                          }
                          sx={{
                            cursor: isSubmitting || isDone ? "default" : "text",
                            verticalAlign: "top",
                            py: 1,
                          }}
                        >
                          {isEditing ? (
                            <TextField
                              inputRef={editRef}
                              size="small"
                              value={row[col.key] || ""}
                              onChange={(e) =>
                                updateCell(row._id, col.key, e.target.value)
                              }
                              onBlur={() => setEditCell(null)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && setEditCell(null)
                              }
                              error={!!err}
                              sx={{
                                width: col.width - 16,
                                "& .MuiInputBase-input": {
                                  py: 0.5,
                                  fontSize: 12,
                                },
                              }}
                            />
                          ) : (
                            <Tooltip title={err || ""} placement="top">
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: col.width - 16,
                                    color: err
                                      ? "error.main"
                                      : row[col.key]
                                        ? "text.primary"
                                        : "text.disabled",
                                    fontStyle:
                                      !row[col.key] && !isSubmitting && !isDone
                                        ? "italic"
                                        : "normal",
                                  }}
                                >
                                  {row[col.key] ||
                                    (isSubmitting || isDone
                                      ? "—"
                                      : "click to edit")}
                                </Typography>
                                {err && (
                                  <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      display: "block",
                                    }}
                                  >
                                    {err}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      );
                    })}

                    {/* Row status */}
                    <TableCell sx={{ verticalAlign: "top", py: 1 }}>
                      {row._status === "success" && (
                        <Chip
                          label="Created"
                          color="success"
                          size="small"
                          variant="outlined"
                          icon={<CheckCircleIcon />}
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      )}
                      {row._status === "error" && (
                        <Tooltip title={row._serverError}>
                          <Chip
                            label="Failed"
                            color="error"
                            size="small"
                            variant="outlined"
                            icon={<ErrorIcon />}
                            sx={{ fontSize: 10, height: 22 }}
                          />
                        </Tooltip>
                      )}
                      {row._status === "uploading" && (
                        <Chip
                          label="Sending…"
                          color="primary"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      )}
                      {row._status === "pending" && !row._skip && !hasErr && (
                        <Chip
                          label="Ready"
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      )}
                      {row._status === "pending" && !row._skip && hasErr && (
                        <Chip
                          label={`${Object.keys(row._errors).length} error${
                            Object.keys(row._errors).length > 1 ? "s" : ""
                          }`}
                          color="error"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      )}
                      {row._skip && (
                        <Chip
                          label="Skipped"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 10, height: 22 }}
                        />
                      )}
                      {!isSubmitting && !isDone && !row._skip && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            display: "block",
                            cursor: "pointer",
                            mt: 0.5,
                            fontSize: 10,
                          }}
                          onClick={() =>
                            setRows((prev) =>
                              revalidateAll(
                                prev.filter((r) => r._id !== row._id),
                                assetTypes,
                              ),
                            )
                          }
                        >
                          remove
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {!isDone
            ? "Click any cell to edit inline · checkbox to skip row"
            : ""}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {isDone ? (
            <Button
              variant="contained"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              }}
              onClick={() => {
                setStage(STAGE.UPLOAD);
                setRows([]);
                setResults(null);
              }}
            >
              Upload another batch
            </Button>
          ) : (
            <>
              <Button
                onClick={onClose}
                color="inherit"
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={validCount === 0 || isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                onClick={handleSubmit}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  minWidth: { xs: "100%", sm: 160 },
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                }}
              >
                {isSubmitting
                  ? `Uploading… ${progress}%`
                  : `Submit ${validCount} asset${validCount !== 1 ? "s" : ""}`}
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────

export default function RegisterAssetModal({
  open,
  onClose,
  onSuccess,
  token,
  activeOrg,
}) {
  const { permissions } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetTypesLoading, setAssetTypesLoading] = useState(false);
  const [assetTypeManagerOpen, setAssetTypeManagerOpen] = useState(false);
  const canManageAssetTypes =
    permissions?.has?.("*") || permissions?.has?.("system.manage");

  const loadAssetTypes = useCallback(async () => {
    setAssetTypesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/asset-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
        },
      });
      setAssetTypes(res.data?.data || []);
    } catch (err) {
      setAssetTypes([]);
    } finally {
      setAssetTypesLoading(false);
    }
  }, [token, activeOrg]);

  // Reset to single tab each open
  useEffect(() => {
    if (open) {
      setTab(0);
      loadAssetTypes();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={tab === 1 ? "lg" : "md"}
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="#fff">
              Register asset
            </Typography>
            <Typography variant="body2" color="#e7e7e7" mt={0.3}>
              {tab === 0
                ? "Add a single hardware unit to inventory"
                : "Upload multiple assets via CSV file"}
            </Typography>
            {tab === 1 ? (
              <Typography
                variant="caption"
                color="#e7e7e7"
                display="block"
                mt={0.6}
              >
                Bulk upload supports both legacy asset_type names and standard
                asset_type_code values. Any extra CSV columns that match
                asset-type field keys will be stored as typed metadata.
              </Typography>
            ) : null}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ mt: -0.5, color: "#e7e7e7" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mt: 1.5,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#e7e7e7",
            },
             "& .Mui-selected": {
      color: "#e7e7e7 !important", // 👈 keep same color on select
    },

    "& .MuiTabs-indicator": {
      backgroundColor: "#F59E0B", // 👈 optional (underline color)
    },
          }}
        >
          <Tab
            icon={<PersonIcon fontSize="small" />}
            iconPosition="start"
            label="Single asset"
          />
          <Tab
            icon={<UploadFileIcon fontSize="small" />}
            iconPosition="start"
            label="Bulk upload (CSV)"
          />
        </Tabs>
      </DialogTitle>

      {tab === 0 && (
        <SingleForm
          token={token}
          activeOrg={activeOrg}
          onClose={onClose}
          onSuccess={onSuccess}
          assetTypes={assetTypes}
          onManageAssetTypes={() => setAssetTypeManagerOpen(true)}
          canManageAssetTypes={canManageAssetTypes}
        />
      )}
      {tab === 1 && (
        <BulkForm
          token={token}
          activeOrg={activeOrg}
          onClose={onClose}
          onSuccess={onSuccess}
          assetTypes={assetTypes}
        />
      )}
      <AssetTypeManagerModal
        open={assetTypeManagerOpen}
        onClose={() => setAssetTypeManagerOpen(false)}
        onSuccess={loadAssetTypes}
        token={token}
        activeOrg={activeOrg}
      />
    </Dialog>
  );
}
